import { FC, useEffect, useState } from 'react';

import { AvatarGroup, Box, MantineProvider, Stack } from '@mantine/core';

import { Container } from '@marketing/components/container/Container';

import { CompanyProfile as CompanyProfileType, File, PermissionCompany } from 'models';

import { Avatar } from 'components/avatar/Avatar';
import { AcceptPartnerModal } from 'components/company-profile/components/AcceptPartnerModal';
import { ActiveArticleModal } from 'components/company-profile/components/ActiveArticleModal';
import { Header } from 'components/company-profile/components/Header';
import { HeaderButtons } from 'components/company-profile/components/HeaderButtons';
import { Notices } from 'components/company-profile/components/Notices';
import { PreviewModal } from 'components/company-profile/components/PreviewModal';
import { ProfileActions } from 'components/company-profile/components/ProfileActions';
import { SwitchTeamNotice } from 'components/company-profile/components/SwitchTeamNotice';
import { UnclaimedProfileDisclaimer } from 'components/company-profile/components/UnclaimedProfileDisclaimer';
import { UpgradeMessageModal } from 'components/company-profile/components/UpgradeMessageModal';
import { useCurrentProfile } from 'components/company-profile/hooks/use-current-profile';
import { ProfileContext, ProfileModal } from 'components/company-profile/hooks/use-profile-context';
import { useProfilePermission } from 'components/company-profile/hooks/use-profile-permission';
import { Home } from 'components/company-profile/pages/Home';
import { currentProfileState } from 'components/company-profile/state/current-profile';
import { CompanyIntercom } from 'components/intercom/types/CompanyIntercom';
import { Page } from 'components/page/Page';
import { StickyActions } from 'components/sticky-actions/StickyActions';
import { useScrollHashIntoView } from 'hooks/dom/use-scroll-hash-into-view';
import { useCompanyTeamMembers } from 'hooks/use-company-team-members';
import { useCurrentTeam } from 'hooks/use-current-team';
import { CompanyProfileService } from 'services/CompanyProfileService';

const CompanyProfile: FC<{ company: CompanyProfileType }> = ({ company }) => {
    useScrollHashIntoView();

    const team = useCurrentTeam();
    const canEdit = useProfilePermission(PermissionCompany.EDIT);
    const { members } = useCompanyTeamMembers(company.id);

    const availableMembers = members.filter((member) => member.image).slice(0, 3);

    const intercomButtonProps = {
        size: 'md',
        ...(availableMembers.length && {
            leftSection: (
                <AvatarGroup spacing="xs">
                    {availableMembers.map((member) => (
                        <Avatar key={member.name} size={24} name={member.name} image={member.image} />
                    ))}
                </AvatarGroup>
            ),
        }),
    };

    useEffect(() => {
        if (!team || !canEdit) return;

        const localSignupUrl = CompanyProfileService.getLocalSignupUrl(team.id);

        if (localSignupUrl) {
            CompanyProfileService.finishLocalSignup(team.id);
        }
    }, [team, company]);

    return (
        <Page hideLicenseAgreement title={company.name}>
            <Notices company={company} />

            <Header
                company={company}
                actions={<ProfileActions company={company} />}
                headerButtons={<HeaderButtons company={company} />}
            />

            <Box w="100%">
                <Container padding="x">
                    <Stack gap="xl" pt="md">
                        <SwitchTeamNotice company={company} />
                        <Home />
                        <UnclaimedProfileDisclaimer company={company} />
                    </Stack>

                    <StickyActions
                        mobile={
                            <CompanyIntercom
                                company={company}
                                buttonProps={{ ...intercomButtonProps, fullWidth: true }}
                            />
                        }
                        desktop={<CompanyIntercom company={company} buttonProps={intercomButtonProps} />}
                    />
                    <PreviewModal />
                    <ActiveArticleModal />
                    <UpgradeMessageModal />

                    {canEdit && <AcceptPartnerModal company={company} />}
                </Container>
            </Box>
        </Page>
    );
};

const WrappedCompanyProfile: FC<{ company: CompanyProfileType }> = ({ company: incomingCompany }) => {
    const company = useCurrentProfile();

    const [openedModal, setOpenedModal] = useState<ProfileModal | null>(null);

    useEffect(() => {
        if (incomingCompany) {
            currentProfileState.profile = incomingCompany;
        }

        return () => {
            currentProfileState.profile = null;
        };
    }, [incomingCompany]);

    if (!company) return null;

    /*
    const getDarkColor = (color: string) => {
        let darkColor = color;

        while (isLightColor(darkColor)) {
            darkColor = darken(darkColor, 0.1);
        }

        return darkColor;
    };

    return (
        <MantineProvider
            theme={{
                colors: {
                    brand: [...Array(5).fill(company.color), ...Array(5).fill(getDarkColor(company.color))] as any,
                },
                defaultGradient: {
                    deg: 130,
                    from: 'primary',
                    to: 'brandOriginal',
                },
            }}
        >
            <CompanyProfile company={company} />
        </MantineProvider>
    );
    */

    return (
        <MantineProvider
            theme={{
                black: '#000000',
            }}
        >
            <ProfileContext.Provider
                value={{
                    openedModal,
                    openModal: (modal) => setOpenedModal(modal),
                    closeModal: () => setOpenedModal(null),
                }}
            >
                <CompanyProfile company={company} />
            </ProfileContext.Provider>
        </MantineProvider>
    );
};

export { WrappedCompanyProfile as CompanyProfile };
